(() => {
  'use strict';

  if (customElements.get('infinite-scroll-products')) return;

  class InfiniteScrollProducts extends HTMLElement {
    constructor() {
      super();
      this.loading = false;
      this.nextUrl = this.dataset.nextUrl || '';
      this.grid = null;
      this.pagination = null;
      this.loader = null;
      this.status = null;
      this.retryButton = null;
      this.observer = null;
      this.controller = null;
      this.loadedUrls = new Set();
      this.handleRetry = this.handleRetry.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      const gridOuter = this.closest('[data-products-grid]');
      this.grid = gridOuter?.querySelector('#CollectionLoop') || null;
      this.pagination = gridOuter?.querySelector('[data-infinite-scroll-fallback]') || null;
      this.loader = gridOuter?.querySelector('.grid__loader') || null;
      this.status = this.querySelector('[data-infinite-scroll-status]');
      this.retryButton = this.querySelector('[data-infinite-scroll-retry]');
      this.nextUrl = this.dataset.nextUrl || '';

      this.retryButton?.addEventListener('click', this.handleRetry);

      if (!this.nextUrl || !this.grid || !('IntersectionObserver' in window) || !('fetch' in window)) {
        return;
      }

      if (this.pagination) this.pagination.hidden = true;

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
        },
        { rootMargin: '800px 0px', threshold: 0 }
      );

      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.retryButton?.removeEventListener('click', this.handleRetry);
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.toggleAttribute('aria-busy', isLoading);
      this.loader?.classList.toggle('is-loading', isLoading);
    }

    setStatus(message = '') {
      if (this.status) this.status.textContent = message;
    }

    handleRetry() {
      if (this.retryButton) this.retryButton.hidden = true;
      if (this.pagination) this.pagination.hidden = true;
      this.loadNextPage();
    }

    finish() {
      this.nextUrl = '';
      this.dataset.nextUrl = '';
      this.observer?.disconnect();
      this.setStatus(this.dataset.completeText || 'Alla produkter har visats.');
      if (this.retryButton) this.retryButton.hidden = true;
      if (this.pagination) this.pagination.hidden = true;
    }

    fail() {
      this.setStatus(this.dataset.errorText || 'Det gick inte att ladda fler produkter.');
      if (this.retryButton) this.retryButton.hidden = false;
      if (this.pagination) this.pagination.hidden = false;
    }

    prepareCard(card) {
      card.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.decoding = 'async';
        image.setAttribute('fetchpriority', 'low');
      });

      card.querySelectorAll('[data-aos]').forEach((element) => {
        element.classList.add('aos-animate');
        element.removeAttribute('data-aos-delay');
      });

      return card;
    }

    async loadNextPage() {
      if (this.loading || !this.nextUrl || !this.grid) return;

      let requestUrl;
      try {
        requestUrl = new URL(this.nextUrl, window.location.origin);
      } catch (error) {
        this.fail();
        return;
      }

      if (requestUrl.origin !== window.location.origin) {
        this.fail();
        return;
      }

      const urlKey = requestUrl.href;
      if (this.loadedUrls.has(urlKey)) {
        this.finish();
        return;
      }

      this.loadedUrls.add(urlKey);
      this.observer?.unobserve(this);
      this.setLoading(true);
      this.setStatus(this.dataset.loadingText || 'Laddar fler produkter…');
      if (this.retryButton) this.retryButton.hidden = true;

      this.controller?.abort();
      this.controller = new AbortController();

      try {
        const response = await fetch(requestUrl.href, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.controller.signal
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const nextDocument = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = nextDocument.querySelector('#CollectionLoop');

        if (!nextGrid) throw new Error('Product grid missing');

        const cards = Array.from(nextGrid.children);
        if (!cards.length) {
          this.finish();
          return;
        }

        const fragment = document.createDocumentFragment();
        cards.forEach((card) => fragment.appendChild(this.prepareCard(card)));
        this.grid.appendChild(fragment);

        const nextLoader = nextDocument.querySelector('infinite-scroll-products[data-next-url]');
        const nextLink = nextDocument.querySelector('[data-infinite-scroll-fallback] .pagination-custom__next[href]');
        this.nextUrl = nextLoader?.dataset.nextUrl || nextLink?.getAttribute('href') || '';
        this.dataset.nextUrl = this.nextUrl;
        this.setStatus('');

        document.dispatchEvent(
          new CustomEvent('theme:infinite-scroll:loaded', {
            detail: { count: cards.length, url: requestUrl.href }
          })
        );

        if (!this.nextUrl) {
          this.finish();
        } else {
          window.setTimeout(() => this.observer?.observe(this), 120);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.loadedUrls.delete(urlKey);
          this.fail();
        }
      } finally {
        this.setLoading(false);
      }
    }
  }

  customElements.define('infinite-scroll-products', InfiniteScrollProducts);
})();
